// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import type { BendTypeName } from '@evolvemep/api-ts';
import { Flex, SegmentedControl, Stack, Text } from '@mantine/core';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { ToggleButton } from 'components/Mantine/ToggleButton';
import { isNil, isNotNil } from 'helpers/isNotNil';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import type { RenderMode } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import { BendRenderCanvas } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendRenderCanvas';

export type BendQuickViewProps = {
  /** When true, hides the layout button even when the bend preview data is available. */
  hideLayoutButton?: boolean;
  bendType: BendTypeName;
  bendPreviewData: BendPreviewSpecifications | null;
  /** @defaultValue '3d' */
  initialPreviewMode?: RenderMode;
};

type QuickViewRenderAreaProps = BendQuickViewProps & {
  renderMode: RenderMode;
  showLabels: boolean;
  showLayout: boolean;
};

const QuickViewRenderArea = ({ bendPreviewData, ...rest }: QuickViewRenderAreaProps) => (
  <>
    <BendRenderCanvas bendPreviewData={bendPreviewData} {...rest} />
    {isNil(bendPreviewData) && (
      <Text size="xs" c="dimmed" fs="italic" pos="absolute" bottom={0} left={0} maw={500}>
        This is a preview of the bend type. A preview using your specified dimensions will be generated once you have
        entered valid values.
      </Text>
    )}
  </>
);

type ViewMode = RenderMode | 'layout';

const viewOptions: { label: string; icon: EvolveIconName; value: ViewMode }[] = [
  { label: '3D Preview', icon: 'PerspectiveView', value: '3d' },
  { label: '2D Front view', icon: 'FrontView', value: '2dFront' },
  { label: '2D Top view', icon: 'TopView', value: '2dTop' },
  { label: 'Layout view', icon: 'LayoutMarkings', value: 'layout' },
];

type BendPreviewControlsProps = {
  previewMode: ViewMode;
  onChangePreviewMode: (value: ViewMode) => void;
  showLabels: boolean;
  toggleLabels: () => void;
  disableLabelToggle: boolean;
  showLayoutControls: boolean;
};

const QuickViewControls = ({
  previewMode,
  onChangePreviewMode,
  showLabels,
  toggleLabels,
  showLayoutControls,
  disableLabelToggle,
}: BendPreviewControlsProps) => {
  const availableViewOptions = useMemo(
    () => (showLayoutControls ? viewOptions : viewOptions.filter((option) => option.value !== 'layout')),
    [showLayoutControls],
  );
  return (
    <Stack gap="xs" pos="absolute" top={0} right={0} align="center" justify="center">
      <SegmentedControl
        orientation="vertical"
        value={previewMode}
        onChange={onChangePreviewMode}
        data={availableViewOptions.map(({ label, icon, value }) => ({
          label: (
            <AgGridStyleTooltip label={label} withArrow>
              <Flex align="center" justify="center" miw={24} mih={38}>
                <EvolveIcon icon={icon} />
              </Flex>
            </AgGridStyleTooltip>
          ),
          value,
        }))}
      />
      <ToggleButton
        checked={showLabels}
        onToggle={toggleLabels}
        disabled={disableLabelToggle}
        actionLabelWhenChecked="Hide labels"
        actionLabelWhenUnchecked="Show labels"
        icon="Label"
        size="xl"
      />
    </Stack>
  );
};

export const BendQuickView = (props: BendQuickViewProps) => {
  const [previewMode, setPreviewMode] = useState<ViewMode>(props.initialPreviewMode ?? '3d');
  const [showLabels, setShowLabels] = useState(true);

  const isLayout = previewMode === 'layout';
  const layoutAvailable = isNotNil(props.bendPreviewData?.layoutDimensions) && !props.hideLayoutButton;
  // "layout" is not actually a render mode. We use the 2D front view in that case.
  // We also fall back to this if the layout mode was selected but became unavailable.
  const renderMode = isLayout ? '2dFront' : previewMode;

  return (
    <>
      <QuickViewControls
        previewMode={isLayout && !layoutAvailable ? renderMode : previewMode}
        onChangePreviewMode={(newMode: ViewMode) => {
          setPreviewMode(newMode);
          if (newMode === 'layout') {
            setShowLabels(true);
          }
        }}
        showLabels={showLabels}
        toggleLabels={() => setShowLabels((v) => !v)}
        showLayoutControls={layoutAvailable}
        disableLabelToggle={isLayout && layoutAvailable}
      />
      <QuickViewRenderArea
        {...props}
        renderMode={renderMode}
        showLabels={showLabels}
        showLayout={isLayout && layoutAvailable}
      />
    </>
  );
};
